import { styled } from '@linaria/react';
import { formatAnnotationLabel, getCategoryColors } from './shared.helpers';
import { fontBySize, paddingBySize, type ChipSize } from './task-chip.type';

type CategoryChipProps = {
  label: string | null | undefined;
  size?: ChipSize;
};

/**
 * Pill rendering of an agent session category label (the `label` field on
 * `flora_annotations`). Color palette is derived from the label value so each
 * category has a distinct visual identity; unknown/missing labels fall back to
 * the neutral unclassified palette.
 *
 * @param {string | null | undefined} label - Raw snake_case label from the backend.
 * @param {ChipSize} size - Padding/font scale shared with TaskChip.
 * @returns {JSX.Element | null} The pill, or `null` if no label is present.
 */
export function CategoryChip({ label, size = 'sm' }: CategoryChipProps) {
  const display = formatAnnotationLabel(label);
  if (!display) return null;

  const colors = getCategoryColors(label);

  return (
    <Pill chipSize={size} textColor={colors.text} borderColor={colors.border} backgroundColor={colors.background}>
      {display}
    </Pill>
  );
}

const Pill = styled.span<{
  chipSize: ChipSize;
  textColor: string;
  borderColor: string;
  backgroundColor: string;
}>`
  display: inline-flex;
  align-items: center;
  padding: ${(props) => paddingBySize[props.chipSize]};
  border: 1px solid ${(props) => props.borderColor};
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  font-family: 'Figtree', sans-serif;
  font-weight: 500;
  font-size: ${(props) => fontBySize[props.chipSize]};
  line-height: 1.2;
  border-radius: 16px;
  white-space: nowrap;
`;
